import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import without from 'lodash/without';

function compare(a, b) {
  // Compare array query params in an unsorted fashion, since order doesn't
  // matter for our purposes.
  if (Array.isArray(a) && Array.isArray(b)) {
    return isEqual([...a].sort(), [...b].sort());
  }

  // Compare other values as strings, to normalize any typecasting differences
  // for integers or booleans (since we haven't done typecasting yet when this
  // compare is done).
  let aString;
  if (a === undefined || a === null) {
    aString = '';
  } else {
    aString = String(a);
  }

  let bString;
  if (b === undefined || b === null) {
    bString = '';
  } else {
    bString = String(b);
  }

  return isEqual(aString, bString);
}

export default function (queryName) {
  return {
    get() {
      // Return the query parameter from the store (rather than directly from
      // the query string). This handles providing a default values, and allows
      // for the form inputs to be retained when navigating to a sub-path that
      // may not contain the query parameters (for example, to allow navigating
      // from "/find/nearest?location=Golden" to "/find/nearest/station/73135"
      // to retain "Golden" in the input).
      return this.$store.getters.getFilterValueWithDefault(queryName);
    },
    set(val) {
      // Duplicate the current filter params (set on any filter changes in
      // App.vue), and merge in the new query parameter value;
      let newVal = val;
      const newQuery = { ...this.$store.getters.getFilters };
      const defaultValue = this.$store.getters.getFilterDefaultValue(queryName);
      const isArray = Array.isArray(val);
      if (isArray) {
        let prevVal = newQuery[queryName];
        if (prevVal === undefined) {
          prevVal = defaultValue;
        }
        // Handle the "all" value in arrays specially as mutually exclusive
        // with the other options. So when a new value is selected that's not
        // all, remove "all". And if "all" is re-selected, then make it the
        // only selected option.
        if (Array.isArray(prevVal) && prevVal.includes('all') && newVal.length > 1) {
          newVal = without(newVal, 'all');
        } else if (newVal.includes('all')) {
          newVal = ['all'];
        }
      }

      if (newVal === undefined || isEqualWith(newVal, defaultValue, compare)) {
        delete newQuery[queryName];
      } else if (isArray && newVal.length === 0) {
        // Serialize empty arrays as null in the query param, since this value
        // still gets explicitly stored in the query param (as "?query" without
        // a value), so we have a way to distinguish all values being unchecked
        // for array types (otherwise Vue doesn't serialize an empty array in
        // the query params).
        newQuery[queryName] = null;
      } else {
        newQuery[queryName] = newVal;
      }

      // If any of the filters change (and the change isn't specifically on the
      // page attribute), then clear the page from the new URL. Since when you
      // change any of your filter parameters, you need to start over viewing
      // the first page of results.
      if (queryName !== 'page' && newQuery.page !== undefined) {
        delete newQuery.page;
      }

      // On filter changes, navigate to the currently active filter route. This
      // may differ from the current URL route, since this would be the last
      // active route associated with filters in the UI (eg, it might be the
      // "/find/nearest" route, even if you've navigated to a station show page
      // from there).
      this.$router.push({
        name: this.$store.getters.getNavActiveFilterRouteName,
        query: newQuery,
      });
    },
  };
}
